/**
 * Single source of truth for rendering timestamps in the user's
 * timezone. Every visible timestamp in the app should pass through one
 * of these helpers.
 *
 * The output respects the user's {@link timezoneStore}-resolved
 * preferences: IANA timezone, locale, and 12h-vs-24h time format.
 *
 * All inputs are interpreted as UTC unless they're explicitly tz-aware
 * (ISO strings with offset, dayjs/moment objects with .utcOffset). The
 * backend stores UTC end-to-end — we render-time convert.
 */
import dayjs from './dayjsInit';
import { parseServerTs } from './parseServerTs';
import {
  getCurrentTz, getCurrentLocale, getCurrentTimeFormat,
} from './timezoneStore';

/** Treat the input as a dayjs object in the user's current timezone.
 *  Returns null for unparseable / null / undefined inputs.
 *
 *  Parsing is delegated to {@link parseServerTs} so that a zone-less
 *  datetime string is read as UTC rather than as browser-local time —
 *  see that module for why. */
const asDayjs = (value) => {
  const ms = parseServerTs(value);
  if (!Number.isFinite(ms)) return null;
  return dayjs(ms).tz(getCurrentTz());
};

/** Build the default time pattern based on user's 12h/24h preference. */
const timePattern = (withSeconds = true) => {
  const t12 = getCurrentTimeFormat() === '12h';
  if (t12) return withSeconds ? 'hh:mm:ss A' : 'hh:mm A';
  return withSeconds ? 'HH:mm:ss' : 'HH:mm';
};

/** "Apr 9, 14:32" / "Apr 9, 2:32 PM" — date + time without year. The
 *  default for inline UI labels in tables / lists. */
export function formatDate(value, opts = {}) {
  const d = asDayjs(value);
  if (!d) return '—';
  const {
    pattern,
    style = 'datetime',      // 'date' | 'time' | 'datetime' | 'long'
    withSeconds = true,
    withYear = false,
  } = opts;
  if (pattern) return d.format(pattern);
  if (style === 'date') {
    return d.format(withYear ? 'MMM D, YYYY' : 'MMM D');
  }
  if (style === 'time') return d.format(timePattern(withSeconds));
  if (style === 'long') return d.format(`MMM D, YYYY ${timePattern(withSeconds)}`);
  // datetime (default)
  return d.format(`MMM D, ${timePattern(withSeconds)}`);
}

/** "2026-05-13 14:32:10" — the ISO-ish form for log lines / monospace
 *  cells where alignment matters. Always 24h. */
export function formatDateIso(value, opts = {}) {
  const d = asDayjs(value);
  if (!d) return '—';
  const withSeconds = opts.withSeconds !== false;
  return d.format(withSeconds ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD HH:mm');
}

/** "14:32:10" — time only, respects 12h/24h preference. */
export function formatTime(value, opts = {}) {
  return formatDate(value, { ...opts, style: 'time' });
}

/** "Apr 9, 2026" — date only. */
export function formatDateOnly(value, opts = {}) {
  return formatDate(value, { ...opts, style: 'date', withYear: opts.withYear !== false });
}

/** "3 hours ago", "in 5 minutes" — relative phrasing. Uses dayjs's
 *  relativeTime plugin which is locale-aware. */
export function formatRelative(value) {
  const d = asDayjs(value);
  if (!d) return '—';
  return d.fromNow();
}

/** "Today 14:32" / "Yesterday 14:32" / "Apr 9, 14:32" — context-aware
 *  phrasing used in feeds and activity ribbons. */
export function formatSmart(value) {
  const d = asDayjs(value);
  if (!d) return '—';
  const now = dayjs().tz(getCurrentTz());
  const time = d.format(timePattern(false));
  if (d.isSame(now, 'day')) return `Today ${time}`;
  if (d.isSame(now.subtract(1, 'day'), 'day')) return `Yesterday ${time}`;
  if (d.year() === now.year()) return d.format(`MMM D, ${timePattern(false)}`);
  return d.format(`MMM D YYYY, ${timePattern(false)}`);
}

/** "Mar 10 14:30 – Mar 12 09:15" — a closed range. */
export function formatDateRange(from, to, opts = {}) {
  const a = asDayjs(from);
  const b = asDayjs(to);
  if (!a && !b) return '—';
  if (!a) return formatDate(to, opts);
  if (!b) return formatDate(from, opts);
  const sameDay = a.isSame(b, 'day');
  if (sameDay) {
    return `${a.format('MMM D')} ${a.format(timePattern(false))} – ${b.format(timePattern(false))}`;
  }
  return `${formatDate(from, opts)} – ${formatDate(to, opts)}`;
}

/** Short name of the resolved zone AT the given instant ("EDT" in
 *  summer, "EST" in winter, "GMT+3", "UTC"). Unlike {@link tzAbbreviation}
 *  this is taken at the timestamp itself, so a time from before a DST
 *  change keeps the label it had. Returns '' for unparseable input. */
export function zoneAbbreviationAt(value) {
  const ms = parseServerTs(value);
  if (!Number.isFinite(ms)) return '';
  const tz = getCurrentTz();
  try {
    const parts = new Intl.DateTimeFormat(getCurrentLocale(), {
      timeZone: tz, timeZoneName: 'short',
    }).formatToParts(new Date(ms));
    const part = parts.find((p) => p.type === 'timeZoneName');
    return part ? part.value : tz;
  } catch (_) {
    return tz || 'UTC';
  }
}

/** {@link formatDate} followed by the zone's short name at that instant:
 *  "Oct 7, 17:54:20 EDT". For times a reader may compare with a clock or
 *  with a notification (alerts, #128) — a bare "17:54" does not say
 *  whose 17:54 it is. */
export function formatDateTz(value, opts = {}) {
  const text = formatDate(value, opts);
  if (text === '—') return text;
  const abbr = zoneAbbreviationAt(value);
  return abbr ? `${text} ${abbr}` : text;
}

/** "2026-10-07 21:54:20 UTC" — the unambiguous form for hover text. */
export function formatUtc(value) {
  const ms = parseServerTs(value);
  if (!Number.isFinite(ms)) return '—';
  return `${dayjs(ms).utc().format('YYYY-MM-DD HH:mm:ss')} UTC`;
}

/** Current tz abbreviation ("PDT", "GMT+1", "UTC") — used as a hint
 *  next to time pickers so users know which clock they're reading. */
export function tzAbbreviation() {
  try {
    const tz = getCurrentTz();
    const fmt = new Intl.DateTimeFormat(getCurrentLocale(), {
      timeZone: tz, timeZoneName: 'short',
    });
    const parts = fmt.formatToParts(new Date());
    const part = parts.find((p) => p.type === 'timeZoneName');
    return part ? part.value : tz;
  } catch (_) {
    return getCurrentTz();
  }
}
